/**
 * @ngdoc directive
 * @name rolesList
 * @module flowingly.runner.setup
 * @description This component is used to display the list of roles
 * @usage
 * ```
     <roles-list></roles-list>
 * ``` 
 */
import angular, { IScope } from 'angular';
import SetupRolesController from './runner.setup.roles.controller';
import { SharedAngular } from '@Client/@types/sharedAngular';
import IRoleDetail from '@Shared.Angular/@types/core/contracts/queryModel/authorisation/roleDetail';

export default class RolesListComponentController {
  static $inject = ['$scope', 'APP_CONFIG', 'roleApiService'];

  private options;
  private gridNeedResize;

  constructor(
    private $scope: IScope,
    private APP_CONFIG: SharedAngular.APP_CONFIG,
    private roleApiService: RoleApiService
  ) {
    $scope.$on(SetupRolesController.ROLES_UPDATED_EVENT, () =>
      this.handleRolesUpdated()
    );

    this.options = {
      dataSource: {
        transport: {
          read: (params) => {
            roleApiService.searchRoles(params.data).then((result) => {
              result.roles.forEach(
                (role: ISetupRoleDetail) =>
                  (role.permissionNames =
                    role.permissions.length < 4
                      ? role.permissions
                          .map((p) => p.name)
                          .sort()
                          .join(', ')
                      : role.permissions
                          .map((p) => p.name)
                          .sort()
                          .slice(0, 3)
                          .join(', ') +
                        ` (+ ${role.permissions.length - 3} more)`)
              );
              params.success({
                data: result.roles,
                total: result.total
              });
              this.gridNeedResize = true;
            });
          }
        },
        schema: {
          data: 'data',
          total: 'total'
        },
        pageSize: APP_CONFIG.gridReportPageSize,
        serverPaging: true,
        serverSorting: true,
        serverFiltering: true,
        serverGrouping: true,
        serverAggregates: true
      },
      noRecords: {
        template: '<teams-no-results></teams-no-results>'
      },
      scrollable: true,
      pageable: true,
      sortable: true,
      resizable: true,
      filterable: {
        mode: 'row'
      },
      columns: this.getColumns()
    };
  }

  handleRolesUpdated() {
    this.$scope.rolesGrid.dataSource.read();
  }

  private getColumns() {
    //Note: field needs to match the name (and case) of the returned data
    return [
      {
        field: 'name',
        title: 'Name',
        filterable: {
          cell: {
            operator: 'contains', //default filtering uses 'equal to' operator, we want to use contains.
            showOperators: false,
            template: function (args) {
              args.element.kendoAutoComplete({
                dataSource: args.dataSource,
                dataTextField: 'name',
                placeholder: 'Search by Name',
                filter: 'contains',
                valuePrimitive: true
              });
            }
          }
        }
      },
      {
        field: 'description',
        title: 'Description',
        filterable: false
      },
      {
        field: 'isDefault',
        title: 'Role Type',
        sortable: false,
        template: function (dataItem) {
          return dataItem.isDefault ? 'Default' : 'Custom';
        },
        filterable: {
          cell: {
            showOperators: false,
            template: function (args) {
              args.element.kendoComboBox({
                dataSource: [
                  { text: 'All', value: '' },
                  { text: 'Custom' },
                  { text: 'Default' }
                ],
                dataTextField: 'text',
                dataValueField: 'value',
                placeholder: 'Search by Role Type'
              });
            }
          }
        }
      },
      {
        field: 'permissionNames',
        title: 'Permissions',
        sortable: false,
        filterable: {
          cell: {
            operator: 'contains',
            showOperators: false,
            template: (args) => {
              this.roleApiService.getAllPermissions().then((permissions) => {
                args.element.kendoComboBox({
                  dataSource: permissions.map((p) => p.name).sort(),
                  placeholder: `Search by Permission`
                });
              });
            }
          }
        }
      },
      {
        title: 'Actions',
        width: '120px',
        filterable: false,
        template:
          "<roles-list-actions role='dataItem' on-roles-updated='$ctrl.handleRolesUpdated()'></roles-list-actions>"
      }
    ];
  }
}

export class RolesListComponent implements angular.IComponentOptions {
  public templateUrl =
    'Client/runner.setup/runner.setup.roles/runner.setup.roles.list.tmpl.html';
  controller = RolesListComponentController;
}

interface ISetupRoleDetail extends IRoleDetail {
  permissionNames: string;
}

angular
  .module('flowingly.runner.setup')
  .component('rolesList', new RolesListComponent());
